import * as React from "react"
import { Check, ChevronDown, X } from "lucide-react"
import { Button } from "../ui/button"
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover"
import { cn } from "./utils"

interface MultiSearchableSelectProps {
  options: { 
    value: string
    label: string
    isActive?: boolean 
  }[]
  placeholder?: string
  values: string[]
  onChange: (values: string[]) => void
  className?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
}

export function MultiSearchableSelect({
  options,
  placeholder = "Select options",
  values,
  onChange,
  className,
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  disabled = false,
}: MultiSearchableSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const toggleValue = (val: string) => {
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val))
    } else {
      onChange([...values, val])
    }
  }

  const selectedLabels = options.filter((o) => values.includes(o.value))
  const filteredOptions = options.filter(option => 
    option.label.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between bg-black/20 border-white/10 text-white hover:bg-white/5 hover:text-white",
            disabled && "opacity-50 cursor-not-allowed",
            className
          )}
        >
          <span className="truncate text-left">
            {selectedLabels.length > 0 
              ? selectedLabels.map(item => item.label).join(', ')
              : <span className="text-slate-400">{placeholder}</span>
            }
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent 
        className="w-[300px] p-0 bg-black/90 border border-white/10 rounded-lg" 
        align="start"
      >
        <Command className="bg-transparent text-white">
          <div className="relative">
            <CommandInput
              placeholder={searchPlaceholder}
              value={search}
              onValueChange={setSearch}
              className="border-b border-white/10"
            />
            {search && (
              <X
                className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 hover:text-white cursor-pointer"
                onClick={() => setSearch("")}
              />
            )}
          </div>

          <CommandEmpty className="py-2 px-4 text-sm text-slate-400">
            {emptyText}
          </CommandEmpty>

          <CommandList className="max-h-[200px] overflow-y-auto">
            {filteredOptions.map((option) => {
              const isSelected = values.includes(option.value)
              const isDisabled = option.isActive === false

              return (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => !isDisabled && toggleValue(option.value)}
                  className={cn(
                    "flex items-center px-4 py-2 text-sm cursor-pointer",
                    isDisabled && "opacity-60 cursor-not-allowed",
                    !isDisabled && "hover:bg-white/10"
                  )}
                  disabled={isDisabled}
                >
                  <div
                    className={cn(
                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                      isSelected 
                        ? "bg-cyan-500/20 border-cyan-500/50" 
                        : "border-white/20 opacity-50"
                    )}
                  >
                    <Check
                      className={cn(
                        "h-3 w-3",
                        isSelected ? "opacity-100 text-cyan-300" : "opacity-0"
                      )}
                    />
                  </div>
                  <span>{option.label}</span>
                </CommandItem>
              )
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}